import { format } from 'date-fns';

import type { ScheduleAvailabilityContext } from '@/lib/api/scheduleAvailability';
import type { AvailabilityWeekday } from '@/lib/api/availability';
import type { Appointment } from '@/types';
import { formatInTimezone } from '@/utils/timezone-utils';

import { isPastScheduleDay, isScheduleTimeSlotInPast } from './form-utils';
import { resolveNutritionistSchedulingTimezone } from './appointment-timezone';

type TimeWindow = { startMinutes: number; endMinutes: number };

const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map((part) => Number(part));
  return hours * 60 + (Number.isFinite(minutes) ? minutes : 0);
};

const minutesToHHmm = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

const subtractWindow = (windows: TimeWindow[], block: TimeWindow): TimeWindow[] => {
  const result: TimeWindow[] = [];

  for (const window of windows) {
    if (block.endMinutes <= window.startMinutes || block.startMinutes >= window.endMinutes) {
      result.push(window);
      continue;
    }

    if (block.startMinutes > window.startMinutes) {
      result.push({ startMinutes: window.startMinutes, endMinutes: block.startMinutes });
    }

    if (block.endMinutes < window.endMinutes) {
      result.push({ startMinutes: block.endMinutes, endMinutes: window.endMinutes });
    }
  }

  return result.filter((window) => window.endMinutes > window.startMinutes);
};

const getAvailabilityWeekday = (date: Date, timezone: string): AvailabilityWeekday => {
  const dayName = formatInTimezone(date, timezone, 'EEEE').toLowerCase();
  return dayName as AvailabilityWeekday;
};

const getScheduleTimezone = (context: ScheduleAvailabilityContext): string =>
  resolveNutritionistSchedulingTimezone(context.schedule.timezone);

/** True when `date` falls within any time-off range (inclusive, date-only). */
export function isDateInTimeOff(
  date: Date,
  timeOff: Array<{ startDate: string; endDate?: string }>,
): boolean {
  const dateKey = format(date, 'yyyy-MM-dd');
  return timeOff.some((entry) => {
    const start = String(entry.startDate || '').trim().slice(0, 10);
    const end = String(entry.endDate || entry.startDate || '').trim().slice(0, 10);
    if (!start) return false;
    return dateKey >= start && dateKey <= end;
  });
}

const getDayWorkingWindows = (
  context: ScheduleAvailabilityContext,
  date: Date,
  timezone: string,
): TimeWindow[] => {
  const weekday = getAvailabilityWeekday(date, timezone);
  const daySchedule = context.schedule.days.find((day) => day.day === weekday);

  if (!daySchedule?.isEnabled || !daySchedule.slots.length) {
    return [];
  }

  let windows: TimeWindow[] = daySchedule.slots.map((slot) => ({
    startMinutes: timeToMinutes(slot.startTime),
    endMinutes: timeToMinutes(slot.endTime),
  }));

  for (const breakEntry of context.breaks) {
    if (!breakEntry.days.includes(weekday)) continue;
    windows = subtractWindow(windows, {
      startMinutes: timeToMinutes(breakEntry.startTime),
      endMinutes: timeToMinutes(breakEntry.endTime),
    });
  }

  return windows;
};

const getAppointmentStart = (appointment: Appointment): Date | null => {
  if (appointment.startTime instanceof Date && !Number.isNaN(appointment.startTime.getTime())) {
    return appointment.startTime;
  }
  if (appointment.date instanceof Date && !Number.isNaN(appointment.date.getTime())) {
    return appointment.date;
  }
  return null;
};

const getAppointmentEnd = (appointment: Appointment, start: Date): Date => {
  if (appointment.endTime instanceof Date && !Number.isNaN(appointment.endTime.getTime())) {
    return appointment.endTime;
  }
  const durationMinutes = Number.isFinite(appointment.duration) ? appointment.duration : 60;
  return new Date(start.getTime() + durationMinutes * 60_000);
};

const getBusyWindowsForDay = (
  date: Date,
  appointments: Appointment[],
  timezone: string,
  excludeAppointmentId?: string,
): TimeWindow[] => {
  const busy: TimeWindow[] = [];
  const dateKey = formatInTimezone(date, timezone, 'yyyy-MM-dd');

  for (const appointment of appointments) {
    if (
      excludeAppointmentId &&
      String(appointment.id) === String(excludeAppointmentId)
    ) {
      continue;
    }

    const start = getAppointmentStart(appointment);
    if (!start) continue;
    if (formatInTimezone(start, timezone, 'yyyy-MM-dd') !== dateKey) continue;

    const end = getAppointmentEnd(appointment, start);
    const [startHours, startMinutes] = formatInTimezone(start, timezone, 'HH:mm').split(':').map(Number);
    const [endHours, endMinutes] = formatInTimezone(end, timezone, 'HH:mm').split(':').map(Number);
    busy.push({
      startMinutes: startHours * 60 + startMinutes,
      endMinutes: endHours * 60 + endMinutes,
    });
  }

  return busy;
};

/** Bookable start times (HH:mm) for a day; step matches session length so each slot is one full session. */
export function getAvailableSlotsForDay(
  context: ScheduleAvailabilityContext,
  date: Date,
  durationMinutes: number,
  slotStepMinutes?: number,
  excludeAppointmentId?: string,
): string[] {
  const timezone = getScheduleTimezone(context);
  const step = slotStepMinutes ?? durationMinutes;
  if (isPastScheduleDay(date) || isDateInTimeOff(date, context.timeOff)) {
    return [];
  }

  let windows = getDayWorkingWindows(context, date, timezone);
  const busyWindows = getBusyWindowsForDay(date, context.appointments, timezone, excludeAppointmentId);

  for (const busy of busyWindows) {
    windows = subtractWindow(windows, busy);
  }

  const slots: string[] = [];

  for (const window of windows) {
    for (
      let minute = window.startMinutes;
      minute + durationMinutes <= window.endMinutes;
      minute += step
    ) {
      const slot = minutesToHHmm(minute);
      if (!isScheduleTimeSlotInPast(slot, date, timezone)) {
        slots.push(slot);
      }
    }
  }

  return [...new Set(slots)].sort();
};

export function dayHasAvailableSlots(
  context: ScheduleAvailabilityContext,
  date: Date,
  durationMinutes: number,
  excludeAppointmentId?: string,
): boolean {
  return getAvailableSlotsForDay(context, date, durationMinutes, undefined, excludeAppointmentId).length > 0;
}

/** Returns yyyy-MM-dd keys for enabled weekdays (not past, not time off). Matches public booking calendar logic. */
export function getEnabledScheduleDayKeys(
  context: ScheduleAvailabilityContext,
  days: Date[],
): Set<string> {
  const keys = new Set<string>();

  for (const day of days) {
    if (isScheduleDayBookable(day, context.schedule.days ?? [], context.timeOff)) {
      keys.add(format(day, 'yyyy-MM-dd'));
    }
  }

  return keys;
}

function isScheduleDayBookable(
  day: Date,
  scheduleDays: Array<{ day: string; isEnabled: boolean }>,
  timeOff: Array<{ startDate: string; endDate?: string }>,
): boolean {
  if (isPastScheduleDay(day) || isDateInTimeOff(day, timeOff)) {
    return false;
  }

  if (scheduleDays.length > 0) {
    const dayName = format(day, 'EEEE').toLowerCase();
    const scheduleDay = scheduleDays.find((entry) => entry.day === dayName);
    if (!scheduleDay?.isEnabled) {
      return false;
    }
  }

  return true;
}

/** @deprecated Prefer {@link getEnabledScheduleDayKeys} — client-side slot math can diverge from the API. */
export function getAvailableScheduleDayKeys(
  context: ScheduleAvailabilityContext,
  days: Date[],
  durationMinutes: number,
  excludeAppointmentId?: string,
): Set<string> {
  const keys = new Set<string>();
  const timezone = getScheduleTimezone(context);

  for (const day of days) {
    if (dayHasAvailableSlots(context, day, durationMinutes, excludeAppointmentId)) {
      keys.add(formatInTimezone(day, timezone, 'yyyy-MM-dd'));
    }
  }

  return keys;
}
